import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, Car, Gem, Users, Shield, TrendingUp, Globe, ChevronRight, Check, Sparkles, Phone, Mail, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PartnerCountrySwitch from '@/components/PartnerCountrySwitch';
import axios from 'axios';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Mumbai skyline at dusk — same Unsplash quality as UAE hero, India-coded.
const HERO_BG = 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=1920&q=80';
const serif = { fontFamily: "'Playfair Display', serif" };
const gold = '#C6A15B';

const INDIA_CITIES = ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Pune', 'Chennai', 'Kolkata', 'Ahmedabad', 'Goa', 'Gurugram', 'Noida'];

const Fade = ({ children, delay = 0, className = '' }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.7, delay }} className={className}>
    {children}
  </motion.div>
);

const BUSINESS_TYPES = [
  { value: 'developer', label: 'Real Estate Developer' },
  { value: 'broker', label: 'Brokerage / Agency' },
  { value: 'agent', label: 'Individual Agent' },
  { value: 'car_dealer', label: 'Luxury Car Dealer' },
  { value: 'yacht_dealer', label: 'Yacht / Marine' },
  { value: 'interior_designer', label: 'Interior Design / Concierge' },
  { value: 'other_business', label: 'Other Luxury Service' },
];

const IndiaPartnerLandingPage = () => {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    contact_person: '', company_name: '', email: '', phone: '', business_type: '', city: '', website: '', description: ''
  });

  // Persist India portal context for the /in/* prefix and signal axios interceptor.
  useEffect(() => {
    try { localStorage.setItem('dreamlife_country', 'india'); } catch { /* ignore */ }
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_person || !form.company_name || !form.email || !form.phone || !form.business_type) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        country_city: form.city ? `${form.city}, India` : 'India',
      };
      delete payload.city;
      await axios.post(`${API}/partner/apply/json`, payload, { headers: { 'X-Portal-Region': 'IN' } });
      setSubmitted(true);
      toast.success('Application submitted! Our India team will reach out shortly.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Submission failed. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const set = (key) => (e) => setForm(p => ({ ...p, [key]: typeof e === 'string' ? e : e.target.value }));

  return (
    <div className="min-h-screen bg-[#060606] text-white" data-testid="india-partner-landing">
      {/* ── Navbar ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#060606]/80 backdrop-blur-xl border-b border-white/[0.04]">
        <div className="max-w-[1200px] mx-auto px-5 h-16 flex items-center justify-between gap-3">
          <Logo
            to={null}
            size="sm"
            wordmarkClassName="text-[#C6A15B]"
            tracking="tracking-[0.12em]"
            sublabel="INDIA"
            testId="india-partner-nav-logo"
          />
          <div className="hidden md:block"><PartnerCountrySwitch active="IN" /></div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/in/partner/login')} className="text-xs text-white/40 hover:text-white transition-colors hidden sm:block">Partner Login</button>
            <button onClick={scrollToForm} className="bg-[#C6A15B] text-black text-xs font-semibold px-5 py-2.5 hover:bg-[#D4B06A] transition-colors" data-testid="india-nav-cta">
              Become a Partner
            </button>
          </div>
        </div>
        <div className="md:hidden border-t border-white/[0.04] px-5 py-2 flex justify-center">
          <PartnerCountrySwitch active="IN" />
        </div>
      </nav>

      {/* ── 1. Hero ── */}
      <section className="relative min-h-[100vh] flex items-center justify-center pt-16" data-testid="india-hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Mumbai skyline" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-[#060606]" />
        </div>
        <div className="relative z-10 max-w-[860px] mx-auto px-6 text-center py-24">
          <Fade>
            <p className="text-[#C6A15B] text-[11px] tracking-[0.35em] uppercase mb-6 font-medium">Partner With ASTIIV BASGAT · India</p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.08] mb-6" style={serif}>
              India's HNI &amp; NRI<br />Luxury Buyer Network
            </h1>
            <p className="text-white/45 text-base sm:text-lg max-w-[600px] mx-auto mb-10 leading-relaxed">
              ASTIIV BASGAT is a luxury lifestyle acquisition platform built for India. We connect Mumbai, Delhi, and Bangalore developers and luxury brands with verified high-net-worth domestic buyers and the global Indian diaspora.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={scrollToForm} className="bg-[#C6A15B] text-black px-10 py-4 text-[13px] font-semibold tracking-[0.15em] uppercase hover:bg-[#D4B06A] transition-all flex items-center justify-center gap-2" data-testid="india-hero-cta-partner">
                Become a Partner <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={scrollToForm} className="border border-white/15 px-10 py-4 text-[13px] tracking-[0.15em] uppercase hover:bg-white/5 transition-all" data-testid="india-hero-cta-demo">
                Book a Demo
              </button>
            </div>
            <div className="flex flex-wrap justify-center gap-6 mt-10 text-[11px] tracking-[0.2em] uppercase text-white/35">
              <span>Mumbai</span><span className="text-white/15">·</span>
              <span>Delhi NCR</span><span className="text-white/15">·</span>
              <span>Bangalore</span><span className="text-white/15">·</span>
              <span>Goa</span><span className="text-white/15">·</span>
              <span>NRI Diaspora</span>
            </div>
          </Fade>
        </div>
      </section>

      {/* ── 2. Problem (India angle) ── */}
      <section className="py-24 sm:py-32 px-6">
        <div className="max-w-[900px] mx-auto text-center">
          <Fade>
            <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">The Problem</p>
            <h2 className="text-3xl sm:text-4xl font-light mb-12" style={serif}>Why India's Luxury Market Needs a New Channel</h2>
          </Fade>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { title: 'Fragmented HNI Reach', desc: 'India\'s top 1% buyers are spread across cities, NRIs, and diaspora — traditional portals miss them entirely.' },
              { title: 'Generic Listings', desc: 'A ₹15 Cr Mumbai penthouse competes with mid-segment listings. Premium positioning is lost.' },
              { title: 'No Lifestyle Context', desc: 'Buyers don\'t want a flat — they want a lifestyle. Cars, watches, travel, concierge — bundled.' },
            ].map((item, i) => (
              <Fade key={item.title} delay={i * 0.1}>
                <div className="p-6 border border-white/[0.06] rounded-xl hover:border-[#C6A15B]/20 transition-all">
                  <h3 className="text-lg font-semibold mb-3" style={serif}>{item.title}</h3>
                  <p className="text-white/35 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Solution (India bundle) ── */}
      <section className="py-24 sm:py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-[900px] mx-auto">
          <Fade>
            <div className="text-center mb-16">
              <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">The ASTIIV BASGAT Solution</p>
              <h2 className="text-3xl sm:text-4xl font-light mb-4" style={serif}>Lifestyle Bundles Tuned for India</h2>
              <p className="text-white/35 max-w-lg mx-auto">We pair India's premium real estate with cars, watches, and concierge — sold as a single, unforgettable experience.</p>
            </div>
          </Fade>
          <Fade delay={0.15}>
            <div className="border border-white/[0.06] rounded-2xl overflow-hidden bg-black/40">
              <div className="bg-gradient-to-r from-[#C6A15B]/10 to-transparent p-6 border-b border-white/[0.04]">
                <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C6A15B]" /><span className="text-[10px] text-[#C6A15B] tracking-[0.2em] uppercase font-semibold">Example Bundle</span></div>
                <h3 className="text-xl font-light mt-1" style={serif}>Worli Sea-Face Dream</h3>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-white/[0.04]">
                {[
                  { icon: Building2, type: 'Property',  name: 'Sea-Facing Penthouse',     price: '₹18.5 Cr',  img: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600&q=80' },
                  { icon: Car,       type: 'Car',       name: 'Range Rover Autobiography', price: '₹3.2 Cr',   img: 'https://images.unsplash.com/photo-1631295868223-63265b40d9e4?w=600&q=80' },
                  { icon: Gem,       type: 'Watch',     name: 'Audemars Piguet RO',        price: '₹1.4 Cr',   img: 'https://images.unsplash.com/photo-1622434641406-a158123450f9?w=600&q=80' },
                  { icon: Users,     type: 'Concierge', name: 'Driver + Chef + Travel',     price: '₹24 L/yr', img: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=600&q=80' },
                ].map(item => (
                  <div key={item.type} className="text-center" data-testid={`india-bundle-${item.type.toLowerCase()}`}>
                    <div className="aspect-[4/3] overflow-hidden border-b border-white/[0.04] relative">
                      <img src={item.img} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" />
                      <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1.5">
                        <item.icon className="w-3 h-3 text-[#C6A15B]" />
                        <span className="text-[9px] text-white/80 uppercase tracking-wider font-medium">{item.type}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-sm font-medium">{item.name}</p>
                      <p className="text-[#C6A15B] text-xs mt-1">{item.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Fade>
        </div>
      </section>

      {/* ── 4. Benefits ── */}
      <section className="py-24 sm:py-32 px-6">
        <div className="max-w-[1000px] mx-auto">
          <Fade><div className="text-center mb-16">
            <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">Why Indian Partners Choose Us</p>
            <h2 className="text-3xl sm:text-4xl font-light" style={serif}>Built for India's Luxury Sellers</h2>
          </div></Fade>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'Verified HNI Buyers', desc: 'Every lead is pre-qualified — KYC done, budget verified. No tyre-kickers.' },
              { icon: Globe, title: 'NRI &amp; Diaspora', desc: 'Tap into the global Indian diaspora — UAE, UK, US, Singapore — actively investing back home.' },
              { icon: TrendingUp, title: 'Premium Positioning', desc: 'Your project sits beside Aston Martin and Patek Philippe — never beside a 1BHK ad.' },
              { icon: Check, title: 'Pay-Per-Result', desc: 'No upfront fees, no monthly retainers. You only pay when a qualified lead converts.' },
            ].map((b, i) => (
              <Fade key={b.title} delay={i * 0.08}>
                <div className="p-6 border border-white/[0.06] rounded-xl hover:border-[#C6A15B]/20 transition-all h-full">
                  <b.icon className="w-8 h-8 text-[#C6A15B] mb-4" />
                  <h3 className="text-base font-semibold mb-2">{b.title}</h3>
                  <p className="text-white/30 text-sm leading-relaxed">{b.desc}</p>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. Partner Types ── */}
      <section className="py-24 sm:py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-[900px] mx-auto text-center">
          <Fade>
            <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">Who We Work With</p>
            <h2 className="text-3xl sm:text-4xl font-light mb-12" style={serif}>India Partner Types</h2>
          </Fade>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Building2, label: 'Developers', sub: 'Mumbai · Delhi · Bangalore' },
              { icon: Briefcase, label: 'Brokerages', sub: 'India HNI Specialists' },
              { icon: Car, label: 'Luxury Cars', sub: 'BMW · Mercedes · Bentley' },
              { icon: Users, label: 'Concierge', sub: 'Chefs · Drivers · Travel' },
            ].map((p, i) => (
              <Fade key={p.label} delay={i * 0.08}>
                <div className="p-6 border border-white/[0.06] rounded-xl hover:border-[#C6A15B]/15 transition-all">
                  <p.icon className="w-8 h-8 text-[#C6A15B] mx-auto mb-3" />
                  <p className="font-semibold text-sm">{p.label}</p>
                  <p className="text-white/25 text-xs mt-1">{p.sub}</p>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. How It Works ── */}
      <section className="py-24 sm:py-32 px-6">
        <div className="max-w-[800px] mx-auto">
          <Fade><div className="text-center mb-16">
            <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">Simple Process</p>
            <h2 className="text-3xl sm:text-4xl font-light" style={serif}>How It Works</h2>
          </div></Fade>
          <div className="space-y-0">
            {[
              { n: '01', title: 'Submit Your Listings', desc: 'Add your projects, cars, or services. Our India onboarding team handles RERA + verification.' },
              { n: '02', title: 'We Create Bundles', desc: 'AI matches your listings with complementary luxury assets and tailors them for HNI / NRI segments.' },
              { n: '03', title: 'Receive Verified Leads', desc: 'High-intent, KYC-verified buyers from India + diaspora — delivered straight to your CRM.' },
            ].map((step, i) => (
              <Fade key={step.n} delay={i * 0.1}>
                <div className="flex gap-6 py-8 border-b border-white/[0.04] last:border-0">
                  <span className="text-[#C6A15B] text-3xl font-light opacity-40 shrink-0 w-12" style={serif}>{step.n}</span>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">{step.title}</h3>
                    <p className="text-white/30 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. Diaspora ── */}
      <section className="py-24 sm:py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-[800px] mx-auto text-center">
          <Fade>
            <Globe className="w-10 h-10 text-[#C6A15B] mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-light mb-4" style={serif}>Domestic + Global Indian Buyers</h2>
            <p className="text-white/35 text-base max-w-lg mx-auto mb-10">
              India's wealth is global. ASTIIV BASGAT channels HNI buyers from across India and the diaspora — actively investing in Indian luxury real estate, cars, and lifestyle.
            </p>
          </Fade>
          <Fade delay={0.1}>
            <div className="flex flex-wrap justify-center gap-3">
              {['Mumbai', 'Delhi', 'Bangalore', 'Goa', 'NRI · UAE', 'NRI · UK', 'NRI · USA', 'NRI · Singapore'].map(r => (
                <span key={r} className="px-4 py-2 border border-white/[0.08] rounded-full text-xs text-white/40">{r}</span>
              ))}
            </div>
          </Fade>
        </div>
      </section>

      {/* ── 8. Positioning ── */}
      <section className="py-24 sm:py-32 px-6">
        <div className="max-w-[700px] mx-auto text-center">
          <Fade>
            <p className="text-[#C6A15B] text-base font-medium mb-6 tracking-wide">Not another property portal.</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light leading-tight" style={serif}>
              India's Luxury Lifestyle<br />Acquisition Platform
            </h2>
            <p className="text-white/30 text-base mt-6 max-w-lg mx-auto leading-relaxed">
              We unify property, automotive, lifestyle, and concierge into one buying experience — built for India's most discerning buyers.
            </p>
          </Fade>
        </div>
      </section>

      {/* ── 9. CTA ── */}
      <section className="py-24 sm:py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-[700px] mx-auto text-center">
          <Fade>
            <h2 className="text-3xl sm:text-4xl font-light mb-4" style={serif}>
              Let&apos;s Build the Future<br />of Luxury Buying in India
            </h2>
            <p className="text-white/30 mb-10 max-w-md mx-auto">Join ASTIIV BASGAT&apos;s exclusive India partner network and unlock a new channel of qualified, high-intent luxury buyers — at home and abroad.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={scrollToForm} className="bg-[#C6A15B] text-black px-10 py-4 text-[13px] font-semibold tracking-[0.15em] uppercase hover:bg-[#D4B06A] transition-all flex items-center justify-center gap-2">
                Become a Partner <ChevronRight className="w-4 h-4" />
              </button>
              <button onClick={scrollToForm} className="border border-white/15 px-10 py-4 text-[13px] tracking-[0.15em] uppercase hover:bg-white/5 transition-all">
                Schedule a Call
              </button>
            </div>
          </Fade>
        </div>
      </section>

      {/* ── 10. Lead Form ── */}
      <section ref={formRef} className="py-24 sm:py-32 px-6" id="india-partner-form" data-testid="india-partner-form-section">
        <div className="max-w-[560px] mx-auto">
          <Fade>
            <div className="text-center mb-10">
              <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">Get Started · India</p>
              <h2 className="text-3xl sm:text-4xl font-light mb-3" style={serif}>Request Partnership Access</h2>
              <p className="text-white/30 text-sm">Fill in your details and our India team will reach out within 24 hours.</p>
            </div>
          </Fade>

          {submitted ? (
            <Fade>
              <div className="text-center p-12 border border-[#C6A15B]/30 rounded-2xl bg-[#C6A15B]/[0.03]" data-testid="india-form-success">
                <Check className="w-12 h-12 text-[#C6A15B] mx-auto mb-4" />
                <h3 className="text-2xl font-light mb-2" style={serif}>Application Received</h3>
                <p className="text-white/40 text-sm mb-6">Our India partnership team will contact you within 24 hours.</p>
                <button onClick={() => navigate('/in/partner/login')} className="text-[#C6A15B] text-sm underline underline-offset-4 hover:text-[#D4B06A]">
                  Go to Partner Login
                </button>
              </div>
            </Fade>
          ) : (
            <Fade delay={0.1}>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="india-partner-application-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Name *</Label>
                    <Input value={form.contact_person} onChange={set('contact_person')} placeholder="Full name" className="bg-white/[0.03] border-white/10 text-white placeholder-white/20 h-12" data-testid="india-form-name" required />
                  </div>
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Company *</Label>
                    <Input value={form.company_name} onChange={set('company_name')} placeholder="Company name" className="bg-white/[0.03] border-white/10 text-white placeholder-white/20 h-12" data-testid="india-form-company" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Email *</Label>
                    <Input type="email" value={form.email} onChange={set('email')} placeholder="email@company.in" className="bg-white/[0.03] border-white/10 text-white placeholder-white/20 h-12" data-testid="india-form-email" required />
                  </div>
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">WhatsApp / Phone *</Label>
                    <Input value={form.phone} onChange={set('phone')} placeholder="+91 XX XXX XXXX" className="bg-white/[0.03] border-white/10 text-white placeholder-white/20 h-12" data-testid="india-form-phone" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Business Type *</Label>
                    <Select value={form.business_type} onValueChange={set('business_type')}>
                      <SelectTrigger className="bg-white/[0.03] border-white/10 text-white h-12" data-testid="india-form-business-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                        {BUSINESS_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">City *</Label>
                    <Select value={form.city} onValueChange={v => setForm(p => ({ ...p, city: v }))}>
                      <SelectTrigger className="bg-white/[0.03] border-white/10 text-white h-12" data-testid="india-form-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                        {INDIA_CITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Website</Label>
                  <Input value={form.website} onChange={set('website')} placeholder="https://yourcompany.in" className="bg-white/[0.03] border-white/10 text-white placeholder-white/20 h-12" data-testid="india-form-website" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-[#C6A15B] text-black hover:bg-[#D4B06A] h-14 text-[13px] font-semibold tracking-[0.15em] uppercase" data-testid="india-form-submit-btn">
                  {submitting ? 'Submitting...' : 'Request Partnership Access'}
                </Button>
                <p className="text-center text-white/15 text-xs mt-2">By submitting, you agree to our terms. No upfront fees.</p>
              </form>
            </Fade>
          )}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-10 px-6 border-t border-white/[0.04]">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo
            to={null}
            size="xs"
            wordmarkClassName="text-[#C6A15B]"
            tracking="tracking-[0.12em]"
            sublabel="INDIA"
            testId="india-partner-footer-logo"
          />
          <div className="flex items-center gap-6">
            <a href="mailto:partners@dreamlife.in" className="flex items-center gap-2 text-white/25 text-xs hover:text-white/50 transition-colors"><Mail className="w-3 h-3" /> partners@dreamlife.in</a>
            <a href="tel:+910000000000" className="flex items-center gap-2 text-white/25 text-xs hover:text-white/50 transition-colors"><Phone className="w-3 h-3" /> +91 XXX XXXX</a>
          </div>
          <p className="text-white/10 text-[10px]">&copy; 2026 ASTIIV BASGAT. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default IndiaPartnerLandingPage;
